import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Right-docked surfaces: `SidePanel` and `SlideOverPane` (spec M0-F7 §6.4).
 *
 * Neither draws a backdrop. That is the observed behaviour and it matters: the item card
 * overlays the grid while the board stays visible and scrolled where it was
 * (`[ui:library/item-04-card-updates.png]`, x 740→1440 = 700 px), and the notifications pane
 * (`[ui:library/shell-06-notifications.png]`, x 840→1440 = 600 px) leaves the page clickable.
 */
import { useCallback, useEffect, useRef, } from 'react';
import { Icon } from '@vibe/core';
import { useOverlayDismiss } from '../hooks/useOverlayDismiss.js';
import { icons } from '../icons/index.js';
import { dimensions } from '../tokens/tokens.js';
import { IconButton } from './wrappers.js';
const FOCUSABLE = 'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';
/**
 * Traps Tab inside `container` while `active`, and restores focus to whatever had it when the
 * surface opened (C4, P3).
 */
export function useFocusTrap(active, container) {
    const opener = useRef(null);
    useEffect(() => {
        if (!active)
            return;
        opener.current = document.activeElement;
        const first = container.current?.querySelector(FOCUSABLE);
        (first ?? container.current)?.focus();
        return () => opener.current?.focus?.();
    }, [active, container]);
    return useCallback((event) => {
        if (event.key !== 'Tab' || !container.current)
            return;
        const focusable = Array.from(container.current.querySelectorAll(FOCUSABLE));
        if (focusable.length === 0)
            return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            last.focus();
        }
        else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first.focus();
        }
    }, [container]);
}
/**
 * The item card. The `✕` is on the **left** of the header, which is monday's layout and not a
 * mistake (`[ui:item-04]`).
 */
export function SidePanel({ open, onClose, width = dimensions.sidePanelWidth, title, headerActions, headerBelow, children, 'aria-label': ariaLabel, 'data-testid': dataTestId, }) {
    const ref = useRef(null);
    const onKeyDown = useFocusTrap(open, ref);
    /**
     * **P-260.** A pane is an overlay, and this one *moves focus into itself* — so when it closes,
     * whatever it was opened from is where focus has to go back to, or it lands on `<body>`. The
     * panel's own `onKeyDown` below still handles Escape typed **inside** it (and stops the event
     * there, so the two never both fire); the hook adds Escape from outside and owns the restore.
     */
    useOverlayDismiss({ open, onClose });
    if (!open)
        return null;
    return (_jsxs("aside", { ref: ref, role: "dialog", "aria-modal": "false", "aria-label": ariaLabel ?? (typeof title === 'string' ? title : 'Panel'), className: "ez-side-panel", "data-testid": dataTestId, style: { width: `min(${width}px, 100vw - var(--sidebar-rail-width))` }, tabIndex: -1, onKeyDown: (event) => {
            if (event.key === 'Escape') {
                event.stopPropagation();
                onClose();
                return;
            }
            onKeyDown(event);
        }, children: [_jsxs("header", { className: "ez-side-panel__header", children: [_jsx(IconButton, { icon: icons.close, ariaLabel: "Close panel", kind: "tertiary", onClick: onClose, "data-testid": "side-panel-close" }), _jsx("div", { className: "ez-side-panel__title", children: title }), headerActions ? _jsx("div", { className: "ez-side-panel__actions", children: headerActions }) : null] }), headerBelow, _jsx("div", { className: "ez-side-panel__body", children: children })] }));
}
/** A right-docked pane that owns its own header and tabs. Also no backdrop (P2). */
export function SlideOverPane({ open, onClose, width = dimensions.paneWidth, title, headerActions, toolbar, children, 'data-testid': dataTestId, }) {
    const ref = useRef(null);
    /** **P-260**, as `SidePanel`: this pane takes focus on open and must give it back on close. */
    useOverlayDismiss({ open, onClose });
    useEffect(() => {
        if (!open)
            return;
        ref.current?.focus();
    }, [open]);
    if (!open)
        return null;
    return (_jsxs("aside", { ref: ref, role: "dialog", "aria-modal": "false", "aria-label": title, className: "ez-slide-over", "data-testid": dataTestId, tabIndex: -1, style: { width: `min(${width}px, 100vw)` }, onKeyDown: (event) => {
            if (event.key === 'Escape') {
                event.stopPropagation();
                onClose();
            }
        }, children: [_jsxs("header", { className: "ez-slide-over__header", children: [_jsx("h2", { className: "ez-slide-over__title", children: title }), _jsxs("div", { className: "ez-slide-over__actions", children: [headerActions, _jsx(IconButton, { icon: icons.close, ariaLabel: `Close ${title}`, kind: "tertiary", onClick: onClose, "data-testid": "slide-over-close" })] })] }), toolbar ? _jsx("div", { className: "ez-slide-over__toolbar", children: toolbar }) : null, _jsx("div", { className: "ez-slide-over__body", children: children })] }));
}
/** 66 px, docked to the bottom of the main region (`[uij:item-03#bar]` `{h: 66}`). */
export function BottomActionBar({ open, summary, children, onClose, 'data-testid': dataTestId, }) {
    if (!open)
        return null;
    return (_jsxs("div", { className: "ez-bottom-bar", role: "toolbar", "aria-label": "Bulk actions", "data-testid": dataTestId, children: [_jsx("div", { className: "ez-bottom-bar__summary", children: summary }), _jsx("div", { className: "ez-bottom-bar__actions", children: children }), onClose ? (_jsx(IconButton, { icon: icons.close, ariaLabel: "Clear selection", kind: "tertiary", onClick: onClose })) : null] }));
}
/** Small helper used by the panes' empty rows. */
export function PaneIcon({ name, size = 16 }) {
    return _jsx(Icon, { icon: icons[name], size: size, ignoreFocusStyle: true });
}
//# sourceMappingURL=panels.js.map